/**
 * Toast Store
 *
 * Centralized toast notification management
 */

import { create } from 'zustand';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
}

interface ToastStore {
  toasts: Toast[];
  addToast: (type: ToastType, message: string, duration?: number) => void;
  removeToast: (id: string) => void;
}

const DEFAULT_DURATIONS: Record<ToastType, number> = {
  success: 5000,
  error: 8000,
  info: 5000,
  warning: 120000
};

export const useToastStore = create<ToastStore>((set) => ({
  toasts: [],
  addToast: (type, message, duration) => {
    const id = `toast-${Date.now()}-${Math.random()}`;
    const resolvedDuration =
      typeof duration === 'number' ? duration : DEFAULT_DURATIONS[type] ?? 5000;
    const toast: Toast = { id, type, message, duration: resolvedDuration };

    set((state) => ({
      toasts: [...state.toasts, toast]
    }));

    // Auto-remove after duration
    if (resolvedDuration > 0) {
      setTimeout(() => {
        set((state) => ({
          toasts: state.toasts.filter((t) => t.id !== id)
        }));
      }, resolvedDuration);
    }
  },
  removeToast: (id) =>
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id)
    }))
}));
